import { BloodTrail, NO_TRAIL, TrailPrints, sameTrail, trailFade, type BloodTrailState, type TrailKind } from "@common/definitions/bloodTrails";
import { FloorTypes } from "@common/utils/terrain";
import { GameConsole } from "../console/gameConsole";
import { Game } from "../game";
import { type Decal } from "../objects/decal";
import { InputManager } from "./inputManager";
import { MapManager } from "./mapManager";
import { t } from "../utils/translations/phrases";

/**
 * Blood trails (1.06, owner decision, 29 October 2026: "once the health is below 25 after direct confrontation, player
 * bleeds trails for about 20 seconds unless his health is above 25 again, after, he just walks in bloody steps for until
 * is health is above 25. Walking on pools of blood, POOLS of blood, not blood steps also gives bloody steps for about 10
 * seconds."), as the page shows them: the server leaves the marks (server/src/bloodTrails.ts) and sends each with its
 * age; here they're tinted as they dry (bright when spilt, wet red, then dark: a print's thin film soonest, a puddle
 * last) and fade at the end of their life, and the HUD says what you're leaving behind you.
 *
 * They're the others' to follow, so they're always drawn: with Blood & gore off, in the colour of mud.
 */

type RGB = readonly [number, number, number];
/** spilt blood's colours (managers/gore.ts's: bright when spilt, wet, dried) */
const FRESH: RGB = [158, 16, 24];
const WET: RGB = [108, 10, 16];
const DRY: RGB = [82, 20, 18];
/** how long each kind takes to dry from wet to dark (s): a print's thin film soonest, a puddle last */
const DRY_SECONDS: Readonly<Record<TrailKind, number>> = { drop: 50, print: 22, puddle: 110 };
/** with gore off: dark wet mud, at full strength (still plain to see on the battlefield's mud: a trail is there to be
 * followed; 1.06's lab: a lighter mud, a little see-through, all but vanished on it) */
const MUD = 0x261a10;

const mix = (a: RGB, b: RGB, k: number): number => {
    const t = Math.min(1, Math.max(0, k));
    return (Math.round(a[0] + (b[0] - a[0]) * t) << 16) | (Math.round(a[1] + (b[1] - a[1]) * t) << 8) | Math.round(a[2] + (b[2] - a[2]) * t);
};

/** a trail's mark's colour `age` ms on: bright for a moment, wet, then drying dark */
export function trailTint(kind: TrailKind, age: number): number {
    const s = age / 1000;
    return s < 1.5 ? mix(FRESH, WET, s / 1.5) : mix(WET, DRY, (s - 1.5) / DRY_SECONDS[kind]);
}

interface Mark {
    readonly kind: TrailKind
    /** performance.now() when it was made */
    readonly born: number
    readonly strength: number
}

class BloodTrailMarksClass {
    private readonly _marks = new Map<Decal, Mark>();
    private _next = 0;
    private _listening = false;

    /** a trail's mark seen (`age`: its whole seconds, as the server wrote it) */
    add(decal: Decal, kind: TrailKind, age: number, strength: number): void {
        // (the age is whole seconds: half of one more on average)
        const mark: Mark = { kind, born: performance.now() - (age + 0.5) * 1000, strength };
        this._marks.set(decal, mark);
        if (!this._listening) {
            this._listening = true;
            GameConsole.variables.addChangeListener("cv_gore", () => this._applyAll(performance.now()));
        }
        this._apply(decal, mark, performance.now(), this._goreOff());
    }

    remove(decal: Decal): void {
        this._marks.delete(decal);
    }

    get size(): number { return this._marks.size; }

    /** every frame: their colours, five times a second */
    update(): void {
        const now = performance.now();
        if (now < this._next || this._marks.size === 0) return;
        this._next = now + 200;
        this._applyAll(now);
    }

    reset(): void {
        this._marks.clear();
    }

    private _goreOff(): boolean {
        try {
            return GameConsole.getBuiltInCVar("cv_gore") === "off";
        } catch {
            return false;
        }
    }

    private _applyAll(now: number): void {
        const off = this._goreOff();
        for (const [decal, mark] of this._marks) this._apply(decal, mark, now, off);
    }

    private _apply(decal: Decal, mark: Mark, now: number, goreOff: boolean): void {
        const age = now - mark.born;
        decal.image.tint = goreOff ? MUD : trailTint(mark.kind, age);
        decal.container.alpha = mark.strength * trailFade(mark.kind, age);
    }
}

export const BloodTrailMarks = new BloodTrailMarksClass();

/** what the HUD's chip says */
type ChipState = "hidden" | "bleeding" | "wounded" | "pool" | "ended";

/** how long THE TRAIL ENDS HERE stays after you've healed above 25 (ms) */
const ENDED_MS = 3200;

class BloodTrailHudClass {
    private _state: BloodTrailState = NO_TRAIL;
    private _endedUntil = 0;
    private _key = "";
    private _root?: { readonly root: HTMLElement, readonly title: HTMLElement, readonly line: HTMLElement };

    /** the server's word (PlayerData.bloodTrail) */
    update(state: BloodTrailState): void {
        const before = this._state;
        this._state = state;
        // healed above 25: the trail ends here
        if (before.wounded && !state.wounded && !Game.activePlayer?.dead) this._endedUntil = performance.now() + ENDED_MS;
        if (state.wounded) this._endedUntil = 0;
        if (!sameTrail(before, state)) this.render();
    }

    /** every frame (the words of the end come and go by themselves; a spectator's, a watcher's or a replay's screen hides it) */
    tick(): void {
        if (this._endedUntil !== 0 && performance.now() >= this._endedUntil) this._endedUntil = 0;
        this.render();
    }

    reset(): void {
        this._state = NO_TRAIL;
        this._endedUntil = 0;
        this._key = "";
        if (this._root) this._root.root.hidden = true;
    }

    private _chip(): ChipState {
        const state = this._state;
        // (yours only, playing: not a spectator's, a lobby watcher's or a replay's, nor after the end; off the big map:
        // 1.06's audit)
        if (Game.activePlayer?.dead || Game.gameOver || Game.spectating || Game.delayedView || Game.replaying || MapManager.expanded) return "hidden";
        if (state.bleeding > 0) return "bleeding";
        if (state.prints === TrailPrints.Wounded) return "wounded";
        if (state.prints === TrailPrints.Pool) return "pool";
        if (this._endedUntil !== 0) return "ended";
        return "hidden";
    }

    render(): void {
        const chip = this._chip();
        const state = this._state;
        // (in the water nothing lies, and it washes the boots: the chip says so, 1.06.1's audit)
        const floor = Game.activePlayer?.floorType;
        const wet = floor !== undefined && FloorTypes[floor]?.overlay === true;
        const key = `${chip}|${state.bleeding}|${state.poolLeft}|${wet}`;
        if (key === this._key) return;
        this._key = key;
        if (chip === "hidden" && !this._root) return;
        const hud = this._root ?? this._make();
        if (!hud) return;
        hud.root.hidden = chip === "hidden";
        if (chip === "hidden") return;
        hud.root.dataset.state = chip;
        const hp = BloodTrail.wounded;
        switch (chip) {
            case "bleeding":
                hud.title.textContent = t("BLEEDING");
                hud.line.textContent = wet
                    ? t("<n> s: the water washes your blood away", { n: state.bleeding })
                    : t("<n> s: your blood leaves a trail. Heal above <hp> to stop it", { n: state.bleeding, hp });
                break;
            case "wounded":
                hud.title.textContent = t("BLOODY BOOTS");
                hud.line.textContent = wet ? t("The water washes the blood off your boots") : t("You leave prints until you heal above <hp>", { hp });
                break;
            case "pool":
                hud.title.textContent = t("BLOODY BOOTS");
                hud.line.textContent = t("<n> s: you stepped in blood", { n: state.poolLeft });
                break;
            case "ended":
                hud.title.textContent = t("THE TRAIL ENDS HERE");
                hud.line.textContent = t("You healed above <hp>", { hp });
                break;
        }
    }

    private _make(): BloodTrailHudClass["_root"] {
        const gameUi = document.getElementById("game-ui");
        if (!gameUi) return undefined;
        const root = document.createElement("div");
        root.id = "blood-hud";
        root.hidden = true;
        // (in Arabic its words read right to left in their box: utils/translations/languageLook.ts)
        root.innerHTML = `<span class="bh-drop" aria-hidden="true"></span><span class="bh-words rtl-words"><span class="bh-title"></span><span class="bh-line"></span></span>`;
        // over the healing items at the bottom left; on a phone (its stick is there) in the top middle's column, under the
        // scopes, the gas's words stacking under it
        const scopes = document.getElementById("scopes-container");
        if (InputManager.isMobile && scopes?.parentElement?.id === "center-top-container") scopes.after(root);
        else gameUi.append(root);
        const find = (selector: string): HTMLElement => root.querySelector<HTMLElement>(selector) ?? root;
        return this._root = { root, title: find(".bh-title"), line: find(".bh-line") };
    }
}

export const BloodTrailHud = new BloodTrailHudClass();
